import Alpine from "alpinejs";
import createDebounced from "lodash.debounce";
import { AlpineComponent } from "@nxtlvlsoftware/alpine-typescript";
import { THROTTLE_SELECTION_MS } from "../timing";

function getValue(name: string) {
  const s = getComputedStyle(document.documentElement);
  const v = s.getPropertyValue(name);
  return parseFloat(v.trim().substring(0, v.length - 2));
}

export const HEAD_HEIGHT = getValue("--var-tree-node-height");
export const LEVEL_INDENT = getValue("--var-tree-node-indent");

export const ANIMATE = false;
export const MAX_LEVEL = 10;
export const STICKY_CLS = "sticky";
export const LAST_STICKY_CLS = "last";
export const SELECTED_CLS = "selected";

export enum SelectionMethod {
  Click,
  Keyboard,
  Auto,
}

export type TreeNodeSelectedEvent = CustomEvent<{
  node: TreeNode;
  method: SelectionMethod;
}>;

function getNode(elem: Element | null | undefined): TreeNode | null {
  if (
    elem &&
    elem.hasAttribute("x-data") &&
    elem.getAttribute("x-data")?.startsWith("treeNode(")
  ) {
    return Alpine.$data(elem as HTMLElement) as TreeNode;
  }
  return null;
}

const THROTTLE_SELECTION = THROTTLE_SELECTION_MS;

export default class TreeNode extends AlpineComponent {
  public id: number;
  public selectUrl: string = "";
  public openUrl: string = "";
  public closeUrl: string = "";
  private _isDestroyed = false;

  constructor(
    public ids: number[],
    public path: number[],
    public n: number,
    public targetId: number,
    public isSelected: boolean = false,
    public isOpen: boolean = false,
    public isSticky: boolean = false,
    public isLastSticky: boolean = false
  ) {
    super();
    this.id = ids[ids.length - 1];
  }

  init() {
    this._isDestroyed = false;
    this.selectUrl = this.$el.getAttribute("data-select-url") ?? "";
    this.openUrl = this.$el.getAttribute("data-open-url") ?? "";
    this.closeUrl = this.$el.getAttribute("data-close-url") ?? "";
  }

  destroy(): void {
    this._isDestroyed = true;
  }

  level() {
    return this.path.length - 1;
  }

  children() {
    return Array.from(this.$refs.children?.children ?? []);
  }

  container() {
    let p = this.$el.parentElement;
    while (p) {
      if (p.classList.contains("tree-root")) {
        return p;
      }
      p = p.parentElement;
    }

    throw "Container not found";
  }

  parent() {
    return getNode(this.$el.parentElement?.parentElement);
  }

  previous(): TreeNode | null {
    return getNode(this.$el.previousElementSibling);
  }

  next() {
    return getNode(this.$el.nextElementSibling);
  }

  firstChild() {
    return getNode(this.children()[0]);
  }

  lastChild() {
    return getNode(this.children().at(-1));
  }

  delta(): number | null {
    if (this.n === 0) return null;
    const children = this.$refs.children.children;

    if (children.length === 0) return null;
    const first = children[0] as HTMLElement;

    const bottom = this.$refs.head.getBoundingClientRect().bottom;
    const top = first.getBoundingClientRect().top;
    return top - bottom;
  }

  select(method: SelectionMethod) {
    this.$dispatch("tree-node-selected", { node: this, method });
    TreeNode.loadTreeItem(this);
  }

  async open() {
    this.isOpen = true;
    await this.$nextTick();
    this.$dispatch("tree-node-opened", this);
  }

  async close() {
    this.isOpen = false;
    this.isSticky = false;
    this.isLastSticky = false;
    await this.$nextTick();
    const animations = this.$refs.children.getAnimations();
    await Promise.all(animations.map((x) => x.finished));
    this.$dispatch("tree-node-closed", this);
  }

  async onClickHead() {
    this.select(SelectionMethod.Click);
    await this.$nextTick();

    if (this.n == 0) return;

    if (this.isSticky) {
      const delta = this.delta();
      if (delta === null) {
        console.error("could not compute delta for sticky item");
        return;
      }

      if (delta === 0) {
        this.close();
        return;
      }

      this.container().scrollBy({ top: delta, behavior: ANIMATE ? "smooth" : "instant" });
      return;
    }

    if (this.isOpen) {
      this.close();
      return;
    }

    this.open();
  }

  static loadTreeItem = createDebounced((self: TreeNode) => {
    if (self._isDestroyed || self.selectUrl.length === 0) return;

    self.$dispatch("select", { value: self.selectUrl });
  }, THROTTLE_SELECTION);
}

(window as any)["checkDelta"] = (index: number | null) => {
  const sticky = document.querySelectorAll(".tree-node.sticky");
  const target = sticky[index ?? sticky.length - 1] as HTMLElement;
  const node = Alpine.$data(target) as TreeNode;

  console.time("delta");
  const value = node.delta();
  console.timeEnd("delta");
  console.log(value);
};
